import {
	ConnectButton,
	getDefaultConfig,
	RainbowKitProvider,
} from '@rainbow-me/rainbowkit'
import { useFetchers } from '@remix-run/react'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { type NodeViewProps } from '@tiptap/core'
import { NodeViewWrapper } from '@tiptap/react'
import { CircleCheck, TriangleAlert, WalletCards } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAccount, WagmiProvider } from 'wagmi'
import { mainnet, polygon, optimism, arbitrum, base } from 'wagmi/chains'
import { getCustomizerWidth } from '#app/components/tiptap/hooks/useCustomizer.js'
import '@rainbow-me/rainbowkit/styles.css'
import { Button } from '#app/components/ui/button.js'
import { Input } from '#app/components/ui/input.js'
import { FORM_KEY } from '#app/routes/_public+/$url.($referrer).js'
import { type PUBLIC_FORM } from '#app/utils/constants.js'
const queryClient = new QueryClient()
const config = getDefaultConfig({
	appName: 'My RainbowKit App',
	projectId: 'a90d90df2f3a106c56381d07b7266529',
	chains: [mainnet, polygon, optimism, arbitrum, base],
	ssr: true,
})

const WalletConnectContent = ({
	required,
	unique,
	to,
}: {
	required: boolean
	to: string
	unique: boolean
}) => {
	const { address } = useAccount()

	const fetcher = useFetchers().find(f => f.key === FORM_KEY)

	const data = fetcher?.data as PUBLIC_FORM

	const [error, setError] = useState(data?.errors?.fields[to])

	useEffect(() => {
		setError(data?.errors?.fields[to])
	}, [data])

	return (
		<>
			<div className="flex w-full">
				<ConnectButton.Custom>
					{({ account, openAccountModal, openConnectModal, mounted }) => {
						const ready = mounted
						if (!ready) return <div>Loading...</div>

						return (
							<div className="flex w-full gap-2">
								{(() => {
									return (
										<div className="flex w-full gap-0">
											<div className="hidden w-full">
												<Input
													name={(unique ? '#_' : '') + to}
													icon="WalletCards"
													required={required}
													value={address ?? ''}
													readOnly
													className="input-tt pointer-events-none w-full !rounded-r-none !border-r-0 !pl-11"
													error={error}
													setError={setError}
												/>
											</div>

											{account?.address ? (
												<Button
													onClick={() => {
														setError('')
														openAccountModal()
													}}
													className="button-secondary-tt h-[45px] w-full"
													type="button"
												>
													{error ? (
														<>
															<TriangleAlert width={16} />
															Wallet already used
														</>
													) : (
														<>
															<CircleCheck width={16} />
															Wallet connected
														</>
													)}
												</Button>
											) : (
												<Button
													onClick={openConnectModal}
													className="button-secondary-tt h-[45px] w-full"
													type="button"
												>
													<WalletCards width={16} />
													Connect wallet
												</Button>
											)}
										</div>
									)
								})()}
							</div>
						)
					}}
				</ConnectButton.Custom>
			</div>
		</>
	)
}

export const Component = (props: NodeViewProps) => {
	const editable = props.editor.isEditable
	const maxWidth = getCustomizerWidth(props)
	const { required, to, unique } = props.node.attrs

	return (
		<NodeViewWrapper
			className="flex flex-col gap-2"
			style={{
				width: String(maxWidth) + 'px',
				maxWidth: '100%',
			}}
		>
			{editable ? (
				<div className="pointer-events-none w-full opacity-70">
					<WagmiProvider config={config}>
						<QueryClientProvider client={queryClient}>
							<RainbowKitProvider>
								<WalletConnectContent
									required={required}
									to={to}
									unique={unique}
								/>
							</RainbowKitProvider>
						</QueryClientProvider>
					</WagmiProvider>
				</div>
			) : (
				<div className="w-full">
					<WagmiProvider config={config}>
						<QueryClientProvider client={queryClient}>
							<RainbowKitProvider>
								<WalletConnectContent
									required={required}
									to={to}
									unique={unique}
								/>
							</RainbowKitProvider>
						</QueryClientProvider>
					</WagmiProvider>
				</div>
			)}
		</NodeViewWrapper>
	)
}

export default Component
